import { useEffect, useRef, useState } from 'react';
import { BellRing, CheckCircle2, ChevronLeft, ChevronRight, Cloud, CloudSync, Coffee, DoorOpen, HelpCircle, Plus, Share, Smartphone, SquarePlus, Star, X } from 'lucide-react';
import { Drawer } from 'vaul';
import { Switch } from '../components/ui/switch';
import { loadSymbols } from '../chart/datafeed';
import { submitFeedback } from '../lib/api/feedback';
import { upsertSymbol } from '../lib/api/symbols';
import { errorMessage } from './AppShell.utils';

const supportUrl = 'https://buymeacoffee.com/quanglm';
const supportWidgetUrl = 'https://www.buymeacoffee.com/widget/page/quanglm?description=Support%20this%20project&color=%23FF813F';

export function MenuView({ signedIn, isAdmin, userLabel, cloudSyncEnabled, syncing, syncDisabled, onCloudSyncChange, onSyncNow, onSignOut }: {
  signedIn: boolean;
  isAdmin: boolean;
  userLabel: string;
  cloudSyncEnabled: boolean;
  syncing: boolean;
  syncDisabled: boolean;
  onCloudSyncChange(enabled: boolean): void;
  onSyncNow(): void;
  onSignOut(): void;
}) {
  const [supportOpen, setSupportOpen] = useState(false);
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const [menuScreen, setMenuScreen] = useState<'main' | 'help' | 'notifications'>('main');
  const [feedbackMessage, setFeedbackMessage] = useState('');
  const [feedbackSaving, setFeedbackSaving] = useState(false);
  const [feedbackResult, setFeedbackResult] = useState<string | null>(null);
  const [syncComplete, setSyncComplete] = useState(false);
  const wasSyncingRef = useRef(false);

  useEffect(() => {
    if (syncing) {
      wasSyncingRef.current = true;
      setSyncComplete(false);
      return undefined;
    }

    if (!wasSyncingRef.current) return undefined;
    wasSyncingRef.current = false;
    setSyncComplete(true);
    const timeout = window.setTimeout(() => setSyncComplete(false), 1800);
    return () => window.clearTimeout(timeout);
  }, [syncing]);

  async function handleSubmitFeedback() {
    const message = feedbackMessage.trim();
    if (!message) {
      setFeedbackResult('Feedback required');
      return;
    }

    setFeedbackSaving(true);
    setFeedbackResult(null);
    try {
      await submitFeedback(message);
      setFeedbackMessage('');
      setFeedbackResult('Feedback sent');
    } catch (err) {
      console.error('submitFeedback:', err);
      setFeedbackResult(errorMessage(err));
    } finally {
      setFeedbackSaving(false);
    }
  }

  return (
    <>
      <div className="menu-view">
        {menuScreen === 'main' && (
          <>
            <button type="button" className="profile-card">
              <span className="profile-avatar">{userLabel.slice(0, 1).toUpperCase()}</span>
              <span className="profile-main">
                <strong>{userLabel}</strong>
                <span className="profile-badge">{signedIn ? 'Account' : 'Local workspace'}</span>
              </span>
            </button>

            <div className="menu-list">
              {isAdmin && <AdminSymbolQuickAdd />}
              {signedIn && (
                <button type="button" className="menu-list-row" onClick={() => setFeedbackOpen(true)}>
                  <Star size={26} strokeWidth={1.9} />
                  <span>Submit feedback</span>
                  <ChevronRight size={22} strokeWidth={2.2} />
                </button>
              )}
              <button type="button" className="menu-list-row" onClick={() => setSupportOpen(true)}>
                <Coffee size={26} strokeWidth={1.9} />
                <span>Buy me a coffee</span>
                <ChevronRight size={22} strokeWidth={2.2} />
              </button>
              {signedIn && (
                <button type="button" className="menu-list-row menu-sync-row" onClick={() => onCloudSyncChange(!cloudSyncEnabled)}>
                  <Cloud size={26} strokeWidth={1.9} />
                  <span>Auto-sync charts</span>
                  <Switch
                    checked={cloudSyncEnabled}
                    onClick={(event) => event.stopPropagation()}
                    onCheckedChange={onCloudSyncChange}
                  />
                </button>
              )}
              {signedIn && (
                <button type="button" className={syncComplete ? 'menu-list-row menu-sync-row menu-sync-row-complete' : 'menu-list-row menu-sync-row'} onClick={onSyncNow} disabled={syncDisabled || syncing}>
                  {syncComplete ? <CheckCircle2 size={26} strokeWidth={2.1} /> : <CloudSync className={syncing ? 'menu-sync-icon-spinning' : undefined} size={26} strokeWidth={1.9} />}
                  <span>{syncing ? 'Syncing charts...' : syncComplete ? 'Charts synced' : 'Sync charts now'}</span>
                </button>
              )}
              <button type="button" className="menu-list-row" onClick={() => setMenuScreen('help')}>
                <HelpCircle size={26} strokeWidth={1.9} />
                <span>Help</span>
                <ChevronRight size={22} strokeWidth={2.2} />
              </button>
              {signedIn && (
                <button type="button" className="menu-list-row sign-out-row" onClick={onSignOut}>
                  <DoorOpen size={27} strokeWidth={1.9} />
                  <span>Sign out</span>
                </button>
              )}
            </div>
          </>
        )}

        {menuScreen === 'help' && (
          <section className="menu-subview" aria-label="Help topics">
            <button type="button" className="menu-back-button" onClick={() => setMenuScreen('main')}>
              <ChevronLeft size={24} strokeWidth={2.2} />
              <span>Menu</span>
            </button>
            <h2>Help</h2>
            <div className="help-topic-list">
              {signedIn && (
                <button type="button" className="help-topic-row" onClick={() => setMenuScreen('notifications')}>
                  <BellRing size={24} strokeWidth={2} />
                  <span>
                    <strong>Set up notifications</strong>
                    <small>Add Chartify to your Home Screen for alarm alerts.</small>
                  </span>
                  <ChevronRight size={22} strokeWidth={2.2} />
                </button>
              )}
            </div>
          </section>
        )}

        {menuScreen === 'notifications' && (
          <section className="menu-subview" aria-label="Alarm notification help">
            <button type="button" className="menu-back-button" onClick={() => {
              setMenuScreen('help');
            }}>
              <ChevronLeft size={24} strokeWidth={2.2} />
              <span>Help</span>
            </button>
            <div className="help-card">
              <div className="help-card-hero" aria-hidden="true">
                <BellRing size={30} strokeWidth={2.1} />
              </div>
              <div className="help-card-copy">
                <strong>Set up notifications</strong>
                <span>On iPhone and iPad, Chartify needs to be added to your Home Screen before Safari can deliver price alarm notifications.</span>
              </div>
              <ol className="help-steps" aria-label="Add Chartify to Home Screen steps">
                <li>
                  <Share size={21} strokeWidth={2} aria-hidden="true" />
                  <span>Open Chartify in Safari and tap Share.</span>
                </li>
                <li>
                  <SquarePlus size={21} strokeWidth={2} aria-hidden="true" />
                  <span>Choose Add to Home Screen.</span>
                </li>
                <li>
                  <Smartphone size={21} strokeWidth={2} aria-hidden="true" />
                  <span>Open Chartify from the Home Screen icon, then enable notifications when creating alarms.</span>
                </li>
              </ol>
            </div>
          </section>
        )}
      </div>

      <Drawer.Root open={feedbackOpen} onOpenChange={(open) => {
        setFeedbackOpen(open);
        if (!open) {
          setFeedbackResult(null);
        }
      }}>
        <Drawer.Portal>
          <Drawer.Overlay className="drawer-overlay" />
          <Drawer.Content className="drawer-content feedback-sheet" aria-describedby={undefined}>
            <div className="sheet-handle" aria-hidden="true" />
            <div className="sheet-header feedback-sheet-header">
              <Drawer.Title asChild><strong>Submit Feedback</strong></Drawer.Title>
              <Drawer.Close asChild>
                <button type="button" className="icon-button" aria-label="Close feedback panel"><X size={24} strokeWidth={2.2} /></button>
              </Drawer.Close>
            </div>
            <div className="feedback-sheet-body">
              <div className="feedback-sheet-copy">
                <strong>{userLabel}</strong>
                <span>Share a bug, idea, or rough edge. Goes straight to the team Discord.</span>
              </div>
              <textarea
                className="feedback-textarea"
                value={feedbackMessage}
                onChange={(event) => setFeedbackMessage(event.target.value)}
                placeholder="What happened? What should change?"
                maxLength={1500}
              />
              <div className="feedback-sheet-footer">
                <span className="feedback-counter">{feedbackMessage.trim().length}/1500</span>
                <button
                  type="button"
                  className="feedback-submit"
                  onClick={() => void handleSubmitFeedback()}
                  disabled={feedbackSaving || !feedbackMessage.trim()}
                >
                  {feedbackSaving ? 'Sending...' : 'Send feedback'}
                </button>
              </div>
              {feedbackResult && <div className="feedback-result">{feedbackResult}</div>}
            </div>
          </Drawer.Content>
        </Drawer.Portal>
      </Drawer.Root>

      <Drawer.Root open={supportOpen} onOpenChange={setSupportOpen}>
        <Drawer.Portal>
          <Drawer.Overlay className="drawer-overlay" />
          <Drawer.Content className="drawer-content support-sheet" aria-describedby={undefined}>
            <div className="sheet-handle" aria-hidden="true" />
            <div className="sheet-header support-sheet-header">
              <Drawer.Title asChild><strong>Buy me a coffee</strong></Drawer.Title>
              <Drawer.Close asChild>
                <button type="button" className="icon-button" aria-label="Close support panel"><X size={24} strokeWidth={2.2} /></button>
              </Drawer.Close>
            </div>
            <div className="support-sheet-body">
              <iframe
                className="support-frame"
                src={supportWidgetUrl}
                title="Buy me a coffee"
                loading="lazy"
                allow="payment *; publickey-credentials-get *"
              />
              <a className="support-open-link" href={supportUrl} target="_blank" rel="noreferrer">Open Buy Me a Coffee in a new tab</a>
            </div>
          </Drawer.Content>
        </Drawer.Portal>
      </Drawer.Root>
    </>
  );
}

type AdminSymbolType = 'crypto' | 'stock' | 'forex';

function AdminSymbolQuickAdd() {
  const [open, setOpen] = useState(false);
  const [symbol, setSymbol] = useState('');
  const [type, setType] = useState<AdminSymbolType>('crypto');
  const [exchange, setExchange] = useState(defaultExchangeForType('crypto'));
  const [description, setDescription] = useState('');
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  async function saveSymbol() {
    const cleanSymbol = symbol.trim().toUpperCase();
    if (!cleanSymbol || !exchange.trim()) {
      setMessage('Symbol and exchange required');
      return;
    }

    setSaving(true);
    setMessage(null);
    try {
      await upsertSymbol({
        symbol: cleanSymbol,
        name: cleanSymbol,
        description: description.trim() || `${cleanSymbol} on ${exchange.trim()}`,
        exchange: exchange.trim(),
        asset_type: type === 'stock' ? 'equity' : type,
        category: type,
      });
      await loadSymbols(true);
      setSymbol('');
      setDescription('');
      setMessage(`Added ${cleanSymbol}`);
      setOpen(false);
    } catch (err) {
      console.error('upsertSymbol:', err);
      setMessage(errorMessage(err));
    } finally {
      setSaving(false);
    }
  }

  return (
    <>
      <button type="button" className="menu-list-row" onClick={() => setOpen(true)}>
        <Plus size={26} strokeWidth={1.9} />
        <span>Add symbol</span>
        <ChevronRight size={22} strokeWidth={2.2} />
      </button>

      <Drawer.Root open={open} onOpenChange={(nextOpen) => {
        setOpen(nextOpen);
        if (!nextOpen) setMessage(null);
      }}>
        <Drawer.Portal>
          <Drawer.Overlay className="drawer-overlay" />
          <Drawer.Content className="drawer-content admin-symbol-sheet" aria-describedby={undefined}>
            <div className="sheet-handle" aria-hidden="true" />
            <div className="sheet-header admin-symbol-sheet-header">
              <Drawer.Title asChild><strong>Add symbol</strong></Drawer.Title>
              <Drawer.Close asChild>
                <button type="button" className="icon-button" aria-label="Close add symbol panel"><X size={24} strokeWidth={2.2} /></button>
              </Drawer.Close>
            </div>

            <section className="admin-symbol-form" aria-label="Admin add symbol">
              <div className="admin-symbol-header">
                <span>
                  <strong>Add symbol</strong>
                  <small>Admin</small>
                </span>
                <button type="button" onClick={() => void saveSymbol()} disabled={saving || !symbol.trim()}>{saving ? 'Saving...' : 'Add'}</button>
              </div>

              <div className="admin-symbol-fields">
                <input
                  value={symbol}
                  onChange={(event) => setSymbol(event.target.value.toUpperCase())}
                  placeholder="BTCUSDT"
                  autoCapitalize="characters"
                />
                <select
                  value={type}
                  onChange={(event) => {
                    const nextType = event.target.value as AdminSymbolType;
                    setType(nextType);
                    setExchange(defaultExchangeForType(nextType));
                  }}
                >
                  <option value="crypto">Crypto</option>
                  <option value="stock">Stock</option>
                  <option value="forex">Forex</option>
                </select>
                <select value={exchange} onChange={(event) => setExchange(event.target.value)}>
                  {exchangeOptionsForType(type).map((option) => (
                    <option key={option} value={option}>{option}</option>
                  ))}
                </select>
              </div>
              <input
                className="admin-symbol-description"
                value={description}
                onChange={(event) => setDescription(event.target.value)}
                placeholder="Description (optional)"
              />
              {message && <div className="admin-symbol-message">{message}</div>}
            </section>
          </Drawer.Content>
        </Drawer.Portal>
      </Drawer.Root>
    </>
  );
}

function defaultExchangeForType(type: AdminSymbolType): string {
  switch (type) {
    case 'stock': return 'NASDAQ';
    case 'forex': return 'OANDA';
    default: return 'Binance';
  }
}

function exchangeOptionsForType(type: AdminSymbolType): string[] {
  switch (type) {
    case 'stock': return ['NASDAQ', 'NYSE'];
    case 'forex': return ['OANDA', 'Yahoo Finance'];
    default: return ['Binance'];
  }
}
